import { useEffect, useMemo, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { clinicService } from "../services/api";

const DEFAULT_TITLE = "Qease";
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ""}/favicon.ico`;
const CACHE_KEY = "qease_clinic_brand_cache_v1";
const inMemoryCache = new Map();
const RESERVED_FIRST_SEGMENTS = new Set([
    "qease",
    "careline",
    "careline-faq",
    "polyclinics",
    "doctors",
    "payment",
    "payment-success",
    "login",
    "super-admin",
    "clinic-admin",
    "doctor-dashboard",
]);

function resolveAssetUrl(path) {
    if (!path || typeof path !== "string") return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const base =
        process.env.REACT_APP_BACKEND_URL ||
        (process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api").replace(/\/api\/?$/, "");
    return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

function getOrCreateFaviconLink() {
    return (
        document.querySelector('link[rel="icon"]') ||
        (() => {
            const link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
            return link;
        })()
    );
}

function parseStoredCache() {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function persistCache(cacheObject) {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
    } catch {
        // Ignore storage limits; in-memory cache still works.
    }
}

function inferPageLabel(pathname, slug) {
    if (!slug) return "Home";
    const rest = pathname.replace(new RegExp(`^/${slug}`), "");
    if (!rest || rest === "/") return "Home";
    if (rest.startsWith("/clinic-admin")) return "Clinic Admin";
    if (rest.startsWith("/dashboard/doctor")) return "Dashboard";
    if (rest.startsWith("/login")) return "Login";
    if (rest.startsWith("/register")) return "Register";
    if (rest.startsWith("/careline")) return "CareLine";
    if (rest.startsWith("/dokter")) return "Dokter";
    if (rest.startsWith("/poliklinik")) return "Poliklinik";
    if (rest.startsWith("/faq")) return "FAQ";
    return "Dashboard";
}

export default function DynamicClinicBranding() {
    const location = useLocation();
    const [clinic, setClinic] = useState(null);
    const [loading, setLoading] = useState(false);

    const slug = useMemo(() => {
        const firstSegment = location.pathname.split("/").filter(Boolean)[0];
        if (!firstSegment || RESERVED_FIRST_SEGMENTS.has(firstSegment)) return null;

        const tenantRouteMatches = [
            "/:slug",
            "/:slug/login",
            "/:slug/register",
            "/:slug/careline",
            "/:slug/poliklinik",
            "/:slug/dokter",
            "/:slug/faq",
            "/:slug/clinic-admin",
            "/:slug/clinic-admin/poli",
            "/:slug/clinic-admin/pasien",
            "/:slug/clinic-admin/antrian",
            "/:slug/clinic-admin/dokter",
            "/:slug/clinic-admin/settings",
            "/:slug/dashboard/doctor",
        ].some((pattern) => !!matchPath({ path: pattern, end: true }, location.pathname));

        return tenantRouteMatches ? firstSegment : null;
    }, [location.pathname]);

    useEffect(() => {
        let active = true;
        if (!slug) {
            setClinic(null);
            setLoading(false);
            return () => {
                active = false;
            };
        }

        const stored = parseStoredCache();
        const cached = inMemoryCache.get(slug) || stored[slug];
        if (cached) {
            setClinic(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }

        clinicService
            .getBySlug(slug)
            .then((res) => {
                if (!active) return;
                if (res.data?.success && res.data?.data) {
                    const brand = res.data.data;
                    setClinic(brand);
                    inMemoryCache.set(slug, brand);
                    persistCache({ ...stored, [slug]: brand });
                }
            })
            .catch(() => {
                if (active && !cached) setClinic(null);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [slug]);

    useEffect(() => {
        const faviconLink = getOrCreateFaviconLink();

        if (!slug) {
            document.title = DEFAULT_TITLE;
            faviconLink.setAttribute("href", DEFAULT_FAVICON);
            return;
        }

        const pageLabel = inferPageLabel(location.pathname, slug);
        if (loading && !clinic) {
            document.title = "Loading... | Qease";
            return;
        }

        const clinicName = clinic?.nama_klinik || "Clinic";
        document.title = `${clinicName} - ${pageLabel} | Qease`;

        const resolvedFavicon = resolveAssetUrl(clinic?.logo_url);
        if (resolvedFavicon) {
            faviconLink.setAttribute("href", resolvedFavicon);
        } else {
            faviconLink.setAttribute("href", DEFAULT_FAVICON);
        }
    }, [slug, clinic, loading, location.pathname]);

    return null;
}

